import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import MatrixRain from '@/components/MatrixRain';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');

  React.useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const [loading, setLoading] = useState(false);

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Digite seu email');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email,
        company_id: selectedCompany || undefined
      };

      const response = await axios.post(`${API}/auth/local-login`, payload);
      // document.cookie = `__session=${response.data.session_token}; path=/; max-age=${7 * 24 * 60 * 60}; samesite=none; secure`;
      localStorage.setItem('authToken', response.data.session_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.session_token}`;

      const userStatus = response.data.user?.status || 'approved';

      if (userStatus === 'pending') {
        toast.info('Cadastro realizado! Aguarde aprovação.');
        navigate('/pending-approval', { state: { user: response.data.user }, replace: true });
      } else if (userStatus === 'rejected') {
        toast.error('Seu acesso foi negado. Entre em contato com o administrador.');
      } else {
        toast.success('Login realizado com sucesso!');
        navigate('/dashboard', { state: { user: response.data.user }, replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Erro ao fazer login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      console.log("Google Credential:", credentialResponse.credential);
      const payload = {
        credential: credentialResponse.credential,
        company_id: selectedCompany || undefined
      };

      const response = await axios.post(`${API}/auth/google`, payload);
      localStorage.setItem('authToken', response.data.session_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.session_token}`;

      const userStatus = response.data.user?.status || 'approved';

      if (userStatus === 'pending') {
        toast.info('Cadastro realizado! Aguarde aprovação.');
        navigate('/pending-approval', { state: { user: response.data.user }, replace: true });
      } else if (userStatus === 'rejected') {
        toast.error('Seu acesso foi negado. Entre em contato com o administrador.');
      } else {
        toast.success('Login realizado com sucesso!');
        navigate('/dashboard', { state: { user: response.data.user }, replace: true });
      }
    } catch (error) {
      console.error('Google Login error:', error);
      let errorMessage = 'DEBUG: Login Error (Verifique o Console)';

      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = `Erro: ${error.message}`;
      } else if (error.request) {
        errorMessage = "Nenhuma resposta do servidor (Erro de Rede/CORS).";
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen md:grid-cols-2">
      {/* Lado esquerdo - Animação Matrix Tech */}
      <div className="hidden md:block relative overflow-hidden">
        {/* Efeito Matrix Rain */}
        <MatrixRain />

        {/* Overlay gradiente */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-transparent to-transparent z-10" />

        {/* Linhas de scan tech */}
        <div
          className="absolute inset-0 z-10 pointer-events-none opacity-20"
          style={{
            background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(232, 223, 213, 0.03) 2px, rgba(232, 223, 213, 0.03) 4px)'
          }}
        />

        {/* Conteúdo sobre a animação */}
        <div className="absolute inset-0 z-20 flex flex-col justify-between p-12">
          {/* Logo AIFT */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <span
                className="text-4xl font-bold text-white tracking-tight"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                AIFT
              </span>
              <div className="absolute -bottom-1 left-0 w-full h-0.5 bg-[#E8DFD5] opacity-60" />
            </div>
          </div>

          {/* Texto principal - Bottom */}
          <div className="space-y-6">
            {/* Efeito de terminal */}
            <div className="font-mono text-xs text-[#E8DFD5]/60 tracking-wider">
              <span className="text-[#E8DFD5]">&gt;</span> initializing.ai_first_transformation
              <span className="animate-pulse">_</span>
            </div>

            <h1
              className="text-6xl font-bold text-white tracking-tight leading-none"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              <span className="text-primary">AI FIRST</span><br />
              <span className="text-[#E8DFD5] opacity-90">TRANSFORMATION</span>
            </h1>

            <p className="text-lg text-white/70 max-w-md leading-relaxed">
              Transformando empresas pela Inteligência Artificial.
              O futuro dos negócios começa aqui.
            </p>

            {/* Stats da jornada */}
            <div className="grid grid-cols-2 gap-6 pt-4">
              <div className="space-y-1">
                <div className="text-3xl font-bold text-[#E8DFD5]">6</div>
                <div className="text-xs text-white/70 uppercase tracking-wide">Módulos</div>
                <div className="text-[10px] text-white/40">Fonte: MIT, Singularity, Gartner, McKinsey</div>
              </div>
              <div className="space-y-1">
                <div className="text-3xl font-bold text-[#E8DFD5]">1</div>
                <div className="text-xs text-white/70 uppercase tracking-wide">Plano</div>
                <div className="text-[10px] text-white/40 leading-tight">
                  Passo a passo para transformação AI
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-3xl font-bold text-[#E8DFD5]">3</div>
                <div className="text-xs text-white/70 uppercase tracking-wide">Projetos</div>
                <div className="text-[10px] text-white/40 leading-tight">
                  Selecionados do seu próprio time
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-3xl font-bold text-[#E8DFD5]">∞</div>
                <div className="text-xs text-white/70 uppercase tracking-wide">Passaporte</div>
                <div className="text-[10px] text-white/40 leading-tight">
                  Aceleração AI First ilimitada
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lado direito - Formulário de Login */}
      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          {/* Logo mobile */}
          <div className="md:hidden flex justify-center mb-8">
            <span
              className="text-3xl font-bold tracking-tight"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              AIFT
            </span>
          </div>

          <div className="space-y-2">
            <h2 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bem-vindo
            </h2>
            <p className="text-muted-foreground">Acesse sua conta para continuar</p>
          </div>

          <form onSubmit={handleLocalLogin} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-form-email-input"
                  className="rounded-none border-x-0 border-t-0 border-b border-input bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-primary"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company">Empresa (Opcional para Login, Obrigatório para Novo Cadastro)</Label>
                <select
                  id="company"
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  className="flex h-10 w-full rounded-none border-x-0 border-t-0 border-b border-input bg-transparent px-0 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ backgroundImage: 'none' }}
                >
                  <option value="" className="bg-background text-muted-foreground">Selecione sua empresa...</option>
                  {companies.map((company) => (
                    <option key={company.company_id} value={company.company_id} className="bg-background">
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full rounded-none font-bold tracking-[0.2em] uppercase text-xs px-6 py-4 bg-primary text-background hover:bg-primary/90 transition-all shadow-[0_0_20px_rgba(249,115,22,0.3)]"
              disabled={loading}
              data-testid="login-form-email-button"
            >
              {loading ? 'Inicializando...' : '[ Acessar Sistema ]'}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">ou continue com</span>
              </div>
            </div>

            <div className="flex justify-center w-full">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => toast.error('Falha no login com Google')}
                theme="outline"
                size="large"
                width="400"
                text="continue_with"
                shape="rectangular"
              />
            </div>

            <p className="text-xs text-center text-muted-foreground">
              Apenas usuários autorizados podem acessar a plataforma
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}